import {
  Avatar,
  Box,
  Button,
  Center,
  Container,
  Group,
  Paper,
  Text,
  Title,
  useMantineTheme
} from '@mantine/core'
import { useMediaQuery } from '@mantine/hooks'
import { IconDoorEnter, IconUserPlus } from '@tabler/icons'
import Link from 'next/link'
import { useRouter } from 'next/router'

import { getUrlString } from '@/lib/utils'
import { Channel as ChannelType, Site as SiteType } from '@/types'

interface Props {
  siteData: SiteType
  channelData: ChannelType
}

export default function WelcomeAuth({ siteData, channelData }: Props) {
  // Hooks
  const router = useRouter()
  const theme = useMantineTheme()
  const isSm = useMediaQuery(`(max-width: ${theme.breakpoints.sm}px)`)

  // Constants
  const { callbackUrl: callbackUrlQuery } = router?.query || {}
  const callbackUrl = getUrlString(callbackUrlQuery)
  const urlSearchParams = new URLSearchParams({
    callbackUrl: (callbackUrl || router?.asPath) as string,
    ...(channelData?.slug ? { channel: channelData.slug as string } : {})
  })
  const newQuery = decodeURIComponent(urlSearchParams.toString())

  const hideSignUp =
    !siteData?.isSignupAllowed ||
    siteData?.privacy === 'private' ||
    channelData?.privacy === 'private'

  return (
    <Container p="xl" size="sm">
      <Center mb="md">
        <Avatar
          src={channelData?.logo}
          alt={channelData?.title}
          size={96}
          sx={{
            borderRadius: '50%',
            boxShadow: 'rgb(63 63 68 / 5%) 0px 0px 0px 1px, rgb(63 63 68 / 15%) 0px 1px 2px 0px'
          }}></Avatar>
      </Center>
      <Center mb="md">
        <Title order={1}>{channelData?.title}</Title>
      </Center>
      <Paper p="lg" withBorder mb="xl">
        <Text
          mb="xs"
          dangerouslySetInnerHTML={{
            __html: channelData?.description
              ? channelData?.description.replace(/\n/g, '<br />')
              : `Seja bem-vindo(a) à loja oficial - ${channelData?.title}!`
          }}
        />
        <Text mb="md">
          Para que você possa acessar este canal será necessário fazer o login. Clique no botão{' '}
          <em>entrar</em> abaixo:
        </Text>
        <Group position="apart" grow={!isSm}>
          <Button
            component={Link}
            href={`/accounts/login?callbackUrl=${callbackUrl || router?.asPath}`}
            size="md"
            fullWidth
            leftIcon={<IconDoorEnter />}>
            Entrar
          </Button>
          {!hideSignUp && (
            <Button
              component={Link}
              href={`/accounts/signup?${newQuery}`}
              size="md"
              fullWidth
              leftIcon={<IconUserPlus />}
              variant="outline">
              Cadastrar
            </Button>
          )}
        </Group>
      </Paper>
      <Box
        hidden={hideSignUp}
        sx={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center', alignItems: 'center' }}>
        <Text size="sm" align="center" mr="xs">
          Ainda não possui uma conta? Clique no botão para fazer o seu cadastro:
        </Text>
        <Button
          component={Link}
          href={`/accounts/signup?${newQuery}`}
          size="xs"
          variant="subtle"
          rightIcon={<IconUserPlus size={20} />}>
          Cadastrar
        </Button>
      </Box>
    </Container>
  )
}
